import React from "react";
import styled from "styled-components";
import Skeleton from "antd/es/skeleton";
import dayjs from "dayjs";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import Tooltip from "common/Tooltips/Tooltip.tsx";
import { FinanceCurrency, FinanceDateRange } from "features/finance/types";
import {
  FinanceTotals,
  formatChange,
  formatMoney,
  formatPercent,
  getChange,
} from "features/finance/utils";

interface FinanceKpisProps {
  totals: FinanceTotals;
  previousTotals?: FinanceTotals;
  previousRange: FinanceDateRange;
  currency: FinanceCurrency;
  periodLabel: string;
  loading?: boolean;
}

interface KpiChange {
  value: number;
  unit?: string;
  isGood: boolean;
  previous: string;
}

interface KpiTileProps {
  label: string;
  value: string;
  meta?: string;
  signedValue?: number | null;
  change?: KpiChange | null;
  previousRangeLabel: string;
  loading?: boolean;
}

const getMoneyChange = (
  current: number,
  previous: number,
  currency: FinanceCurrency,
  higherIsGood = true
): KpiChange | null => {
  const value = getChange(current, previous);
  if (value === null) return null;
  return {
    value,
    isGood: higherIsGood ? value >= 0 : value <= 0,
    previous: formatMoney(previous, currency),
  };
};

const getMarginChange = (
  current: number | null,
  previous: number | null
): KpiChange | null => {
  if (current === null || previous === null) return null;
  return {
    value: current - previous,
    unit: " pp",
    isGood: current >= previous,
    previous: formatPercent(previous),
  };
};

const formatRange = (range: FinanceDateRange) =>
  `${dayjs(range.start_date).format("D MMM YYYY")} – ${dayjs(
    range.end_date
  ).format("D MMM YYYY")}`;

const FinanceKpis = ({
  totals,
  previousTotals,
  previousRange,
  currency,
  periodLabel,
  loading,
}: FinanceKpisProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const previousRangeLabel = formatRange(previousRange);
  const previous = previousTotals;

  return (
    <Grid>
      <KpiTile
        label={t("finance.kpis.total_income")}
        value={formatMoney(totals.income, currency)}
        meta={periodLabel}
        change={
          previous && getMoneyChange(totals.income, previous.income, currency)
        }
        previousRangeLabel={previousRangeLabel}
        loading={loading}
      />
      <KpiTile
        label={t("finance.kpis.total_expenses")}
        value={formatMoney(totals.expenses, currency)}
        meta={periodLabel}
        change={
          previous &&
          getMoneyChange(totals.expenses, previous.expenses, currency, false)
        }
        previousRangeLabel={previousRangeLabel}
        loading={loading}
      />
      <KpiTile
        label={t("finance.kpis.net_profit")}
        value={formatMoney(totals.net, currency)}
        meta={periodLabel}
        signedValue={totals.net}
        change={previous && getMoneyChange(totals.net, previous.net, currency)}
        previousRangeLabel={previousRangeLabel}
        loading={loading}
      />
      <KpiTile
        label={t("finance.kpis.profit_margin")}
        value={formatPercent(totals.margin)}
        meta={t("finance.kpis.of_income")}
        signedValue={totals.margin}
        change={previous && getMarginChange(totals.margin, previous.margin)}
        previousRangeLabel={previousRangeLabel}
        loading={loading}
      />
    </Grid>
  );
};

export default FinanceKpis;

const KpiTile = ({
  label,
  value,
  meta,
  signedValue,
  change,
  previousRangeLabel,
  loading,
}: KpiTileProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  return (
    <Tile>
      <Label>{label}</Label>
      {loading ? (
        <Skeleton.Input active={true} size="small" style={{ width: 120 }} />
      ) : (
        <Value $signedValue={signedValue}>{value}</Value>
      )}
      <Meta>
        {meta}
        {change && !loading && (
          <Tooltip
            title={t("finance.kpis.previous_value", {
              value: change.previous,
              range: previousRangeLabel,
            })}
          >
            <Change $isGood={change.isGood}>
              {formatChange(change.value, change.unit)}{" "}
              <ChangeNote>{t("finance.kpis.vs_previous")}</ChangeNote>
            </Change>
          </Tooltip>
        )}
      </Meta>
    </Tile>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
`;
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 16px 20px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  @media (max-width: 600px) {
    padding: 12px 14px;
  }
`;
const Label = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Value = styled.span<{ $signedValue?: number | null }>`
  font-size: 24px;
  line-height: 30px;
  font-weight: 560;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) =>
    directionColor(getNewLookTokens(p.theme), p.$signedValue ?? undefined)};
  @media (max-width: 600px) {
    font-size: 19px;
    line-height: 26px;
  }
`;
const Meta = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Change = styled.span<{ $isGood: boolean }>`
  font-weight: 500;
  cursor: default;
  color: ${(p) =>
    p.$isGood ? getNewLookTokens(p.theme).up : getNewLookTokens(p.theme).down};
`;
const ChangeNote = styled.span`
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
